import React, {
    type HTMLAttributes,
    type ReactElement,
    useCallback,
    useEffect,
    useRef
} from 'react';

import { devError } from '../../utils';

import type {
    AnimationConfigWithData,
    AnimationConfigWithPath,
    AnimationItem,
    RendererType
} from 'lottie-web';

type OmitAnimationConfig = 'container' | 'animationData' | 'loop' | 'autoplay';

export type AnimationOptions<T extends RendererType = 'svg'> =
    | Omit<AnimationConfigWithPath<T>, OmitAnimationConfig>
    | Omit<AnimationConfigWithData<T>, OmitAnimationConfig>;

export type AnimationData = Record<string, unknown>;

type AnimationSrc = {
    /**
     * Receives a `import('*.lottie.json')` statement and
     * loads the animation data asynchronously.
     */
    src: Promise<{ default: AnimationData }>;
};

type AnimationSrcSync = {
    /**
     * Most of the time you should use `src` instead of `srcSync`.
     * This is useful when you want to load the animation data
     * at build time for things like loaders animations.
     *
     * For other cases, use `src` instead so your animation data
     * is loaded asynchronously and not included in the main bundle.
     */
    srcSync: AnimationData;
};

export type AnimationProps<T extends RendererType = 'svg'> = {
    options?: AnimationOptions<T>;
    height?: string | number;
    width?: string | number;
    loop?: boolean | number;
    autoplay?: boolean;
} & HTMLAttributes<HTMLDivElement> &
    Partial<AnimationSrc> &
    Partial<AnimationSrcSync> &
    (AnimationSrc | AnimationSrcSync);

const getSize = (initial: string | number | undefined): string => {
    if (typeof initial === 'number') {
        return `${initial}px`;
    }
    return initial ?? '100%';
};

export const Animation = <T extends RendererType = 'svg'>({
    options,
    width,
    height,
    style,
    loop = false,
    autoplay = true,
    src,
    srcSync,
    ...rest
}: AnimationProps<T>): ReactElement => {
    const containerRef = useRef<HTMLDivElement>(null);
    const anim = useRef<AnimationItem>();

    const destroy = () => {
        anim.current?.destroy();
    };

    const loadAnimation = useCallback(
        async (animationData: AnimationData) => {
            if (containerRef.current && animationData) {
                destroy();

                try {
                    const { default: lottie } = await import('lottie-web');

                    anim.current = lottie.loadAnimation({
                        container: containerRef.current,
                        renderer: 'svg',
                        animationData,
                        loop,
                        autoplay,
                        ...options
                    });
                } catch (error) {
                    devError('Failed to load lottie animation', error);
                }
            }
        },
        [options, loop, autoplay]
    );

    useEffect(() => {
        let cancelled = false;
        if (srcSync) {
            loadAnimation(srcSync);
        } else if (src) {
            src.then((value) => {
                if (!cancelled) {
                    loadAnimation(value.default);
                }
            }).catch((error) => {
                if (!cancelled) {
                    devError('Failed to load lottie animation', error);
                }
            });
        }
        return () => {
            cancelled = true;
            destroy();
        };
    }, [loadAnimation, src, srcSync]);

    useEffect(() => {
        return () => {
            destroy();
        };
    }, []);

    return (
        <div
            ref={containerRef}
            style={{
                width: getSize(width),
                height: getSize(height),
                overflow: 'hidden',
                margin: '0 auto',
                outline: 'none',
                ...style
            }}
            {...rest}
        />
    );
};
